import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useWebSocketChannel } from './useWebSocket';
import type { StagePresenceUser } from '../types/websocket.types';

/**
 * Хук для отслеживания пользователей, работающих на том же этапе.
 *
 * При монтировании подписывается на канал `presence:<stageKey>` через WebSocket.
 * Бэкенд отвечает событием STAGE_PRESENCE с текущим списком пользователей,
 * а при изменениях рассылает USER_JOINED_STAGE / USER_LEFT_STAGE.
 *
 * Данные в кэше React Query обновляются через WebSocketProvider.handleStagePresence
 * — без дополнительных HTTP-запросов.
 *
 * @param stageKey - Идентификатор этапа, например 'sales-stage1', 'purchase-stage1', 'references-module2'
 *
 * @example
 * const { users } = useStagePresence('sales-stage1');
 */
export function useStagePresence(stageKey: string) {
  const queryClient = useQueryClient();

  // Подписываемся на presence-канал этапа.
  // Хук useWebSocketChannel автоматически отписывается при размонтировании.
  useWebSocketChannel(`presence:${stageKey}`);

  const { data: users = [] } = useQuery<StagePresenceUser[]>({
    queryKey: ['presence', stageKey],
    // Данные поступают через WS (queryClient.setQueryData в WebSocketProvider),
    // queryFn нужен только для инициализации пустого массива.
    queryFn: () => queryClient.getQueryData<StagePresenceUser[]>(['presence', stageKey]) ?? [],
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return { users };
}
